import { Column, Icon } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import type { AnalyticsTier, Dashboard, QuickView } from 'lib-analytics-types';
import { useHasAccess } from 'lib-shared-access-control';
import { CSSProperties, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUpdateEffect } from 'react-use';

import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { articles } from 'utils/elevio';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useDelayedState } from 'utils/useDelayedState';
import { useServerStorage } from 'utils/useServerStorage';

import { analyticsActions } from '../../analytics.actions';
import { AnalyticsDocumentationButton } from '../documentation/analyticsDocumentationButton';

import styles from './analyticsSidenav.module.scss';
import {
  AnalyticsSidenavItemLink,
  AnalyticsSidenavItem as AnalyticsSidenavItemT,
  AnalyticsSidenavSection as AnalyticsSidenavSectionT,
  AnalyticsSidenavSubItem,
} from './analyticsSidenav.types';
import { AnalyticsSidenavItem } from './components/item/analyticsSidenavItem';
import { AnalyticsSidenavSection } from './components/section/analyticsSidenavSection';
import { AnalyticsSidenavTabs } from './components/tabs/analyticsSidenavTabs';

type AnalyticsSidenavProps = {
  isExpandable?: boolean;
};

export const AnalyticsSidenav = ({
  isExpandable = true,
}: AnalyticsSidenavProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const flags = useFeatureFlag(['analytics-custom-dashboards']);
  const hasAdvancedInsightsAccess =
    useHasAccess('analytics') && flags.analyticsCustomDashboards;
  const storage = useServerStorage('user');
  const { tier } = useAppSelector((state) => state.analytics.ui);

  const [isAnimating, setIsAnimating] = useState(false);
  const [isExpanded, setIsExpanded] = useDelayedState(false, 100);
  const [keepExpanded, setKeepExpanded] = useState(false);

  useUpdateEffect(() => {
    setIsAnimating(true);

    const timer = setTimeout(
      () => setIsAnimating(false),
      analyticsSidenavConfig.transitionDuration,
    );
    return () => clearTimeout(timer);
  }, [isExpanded]);

  const dashboards = analyticsApi.endpoints.readDashboards.useQueryState(
    undefined,
    {
      selectFromResult: ({ data }) =>
        data?.data.dashboards.filter((dashboard) => dashboard.displayable) ??
        [],
    },
  );

  const { quickViews, isLoading } = analyticsApi.useReadFavoriteQuickViewsQuery(
    undefined,
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        quickViews: data?.data.quick_views ?? [],
      }),
    },
  );

  const isActiveDashboard = useCallback(
    (dashboard: Dashboard): boolean => {
      return pathname === `/analytics/dashboard/${dashboard.slug}`;
    },
    [pathname],
  );

  const isActiveQuickView = useCallback(
    (quickView: QuickView): boolean => {
      return pathname === `/analytics/quick-view/${quickView.id}`;
    },
    [pathname],
  );

  const getDashboardLinkOptions = useCallback(
    (dashboard: Dashboard): AnalyticsSidenavItemLink['options'] => ({
      path: '/analytics/dashboard/:slug',
      params: {
        path: {
          slug: dashboard.slug,
        },
      },
    }),
    [],
  );

  const getGroupedDashboards = useCallback(
    (tier?: AnalyticsTier) =>
      dashboards
        .filter((dashboard) => dashboard.tier === tier)
        .reduce((result, dashboard) => {
          if (dashboard.group) {
            result.set(dashboard.group, [
              ...(result.get(dashboard.group) ?? []),
              dashboard,
            ]);
          } else {
            result.set(dashboard.title, [dashboard]);
          }

          return result;
        }, new Map<string, readonly Dashboard[]>()),
    [dashboards],
  );

  const favoritesSection = useMemo((): AnalyticsSidenavSectionT => {
    const getLinkOptions = (
      quickView: QuickView,
    ): AnalyticsSidenavItemLink['options'] => ({
      path: '/analytics/dashboard/custom/:id',
      params: {
        path: {
          id: quickView.id,
        },
      },
    });

    const isEmpty = !isLoading && !quickViews.length;

    return {
      title: 'Favorites',
      bordered: true,
      items: [
        {
          type: 'accordion',
          title: 'Favorites',
          icon: 'star',
          active: false,
          options: {
            isLoading,
            isExpanded:
              storage.data.analytics?.sidenav?.isFavoritesExpanded ?? false,
            onToggle() {
              storage.set({
                analytics: (draft) => {
                  draft.sidenav = draft.sidenav ?? {
                    isFavoritesExpanded: false,
                  };

                  draft.sidenav.isFavoritesExpanded =
                    !draft.sidenav.isFavoritesExpanded;
                },
              });
            },
            subItems: [
              ...quickViews.map(
                (quickView) =>
                  ({
                    title: quickView.name,
                    active: isActiveQuickView(quickView),
                    options: getLinkOptions(quickView),
                  }) satisfies AnalyticsSidenavSubItem,
              ),
              ...(isEmpty
                ? ([
                    {
                      title: 'No favorites yet',
                      active: false,
                      disabled: true,
                      options: {
                        path: '/analytics/dashboards',
                      },
                    },
                  ] satisfies AnalyticsSidenavSubItem[])
                : []),
              {
                title: 'All saved dashboards',
                active: pathname === '/analytics/dashboards',
                options: {
                  path: '/analytics/dashboards',
                },
                textProps: {
                  variant: '1',
                },
              },
            ],
            action: (
              <AnalyticsDocumentationButton
                type="article"
                articleId={articles.analytics.quickViews}
              />
            ),
          },
        },
      ],
    };
  }, [isActiveQuickView, isLoading, pathname, quickViews, storage]);

  const tiersSection = useMemo(
    (): AnalyticsSidenavSectionT => ({
      title: 'Tiers',
      items: [
        {
          type: 'button',
          title: 'Analytics',
          active: tier === 'analytics',
          options: {
            onClick: () =>
              dispatch(analyticsActions.setUI({ ui: { tier: 'analytics' } })),
          },
        },
        ...(hasAdvancedInsightsAccess
          ? ([
              {
                type: 'button',
                title: 'Insights',
                active: tier === 'insights',
                options: {
                  onClick: () => {
                    const { isAdvancedInsightsTeaserSeen = false } =
                      storage.data.analytics ?? {};

                    dispatch(
                      analyticsActions.setUI({
                        ui: { tier: 'insights' },
                      }),
                    );

                    if (!isAdvancedInsightsTeaserSeen) {
                      setKeepExpanded(true);

                      setTimeout(async () => {
                        const { isDismissed } = await dispatch(
                          appActions.showModal({
                            modal: { type: 'analytics:advancedInsightsTeaser' },
                          }),
                        );

                        if (isDismissed) {
                          setKeepExpanded(false);

                          storage.set({
                            analytics: (draft) => {
                              draft.isAdvancedInsightsTeaserSeen = true;
                            },
                          });
                        }
                      }, 500);
                    }
                  },
                },
              },
            ] satisfies AnalyticsSidenavItemT[])
          : []),
      ],
    }),
    [dispatch, hasAdvancedInsightsAccess, storage, tier],
  );

  const analyticsDashboardsSection = useMemo((): AnalyticsSidenavSectionT => {
    const groupedDashboards = getGroupedDashboards('analytics');

    return {
      title: 'Analytics',
      items: Array.from(groupedDashboards).map(([group, dashboards]) => {
        if (dashboards.length > 1) {
          return {
            type: 'header',
            title: group,
            icon: dashboards[0].icon,
            active: false,
            options: {
              subItems: dashboards.map((dashboard) => ({
                title: dashboard.slug === 'channels' ? 'All' : dashboard.title,
                active: isActiveDashboard(dashboard),
                options: getDashboardLinkOptions(dashboard),
              })),
            },
          } satisfies AnalyticsSidenavItemT;
        }

        return {
          type: 'link',
          title: group,
          icon: dashboards[0].icon,
          active: isActiveDashboard(dashboards[0]),
          options: getDashboardLinkOptions(dashboards[0]),
        };
      }),
    };
  }, [getDashboardLinkOptions, getGroupedDashboards, isActiveDashboard]);

  const advancedInsightsDashboardsSection =
    useMemo((): AnalyticsSidenavSectionT => {
      const groupedDashboards = getGroupedDashboards('insights');

      const intelligenceRemoteRoutesMap = {
        'conversation-analysis': '/analytics/dashboard/insights/conversations',
        'activity-analysis': '/analytics/dashboard/insights/activity',
      };

      return {
        title: 'Insights',
        items: Array.from(groupedDashboards).map(([group, dashboards]) => {
          if (dashboards.length > 1) {
            return {
              type: 'header',
              title: group,
              icon: dashboards[0].icon,
              active: false,
              options: {
                subItems: [
                  ...dashboards.map((dashboard) => {
                    if (intelligenceRemoteRoutesMap[dashboard.slug]) {
                      const path = intelligenceRemoteRoutesMap[dashboard.slug];

                      return {
                        title: dashboard.title,
                        active: pathname.startsWith(path),
                        options: { path },
                      };
                    }

                    return {
                      title: dashboard.title,
                      active: isActiveDashboard(dashboard),
                      options: getDashboardLinkOptions(dashboard),
                    };
                  }),

                  ...(group === 'Advanced insights'
                    ? ([
                        {
                          title: 'Custom dashboards',
                          active: pathname === '/analytics/dashboards',
                          options: { path: '/analytics/dashboards' },
                        },
                      ] satisfies AnalyticsSidenavSubItem[])
                    : []),
                ],
              },
            } satisfies AnalyticsSidenavItemT;
          }

          return {
            type: 'link',
            title: group,
            icon: dashboards[0].icon,
            active: isActiveDashboard(dashboards[0]),
            options: getDashboardLinkOptions(dashboards[0]),
          };
        }),
      };
    }, [
      getDashboardLinkOptions,
      getGroupedDashboards,
      isActiveDashboard,
      pathname,
    ]);

  const dashboardSections = useMemo(
    (): readonly AnalyticsSidenavSectionT[] => [
      ...(tier === 'analytics' ? [analyticsDashboardsSection] : []),

      ...(hasAdvancedInsightsAccess && tier === 'insights'
        ? [advancedInsightsDashboardsSection]
        : []),
    ],
    [
      analyticsDashboardsSection,
      hasAdvancedInsightsAccess,
      advancedInsightsDashboardsSection,
      tier,
    ],
  );

  const footerSection = useMemo(
    (): AnalyticsSidenavSectionT => ({
      title: 'More',
      items: [
        {
          type: 'button',
          title: 'Send feedback',
          icon: 'paper-plane',
          active: false,
          options: {
            onClick: () =>
              dispatch(
                appActions.showModal({
                  modal: {
                    type: 'app:feedbackForm',
                    arg: {
                      title: 'Analytics Feedback',
                      team: 'insights',
                    },
                  },
                }),
              ),
          },
        },
      ],
    }),
    [dispatch],
  );

  return (
    <nav
      aria-label="Analytics Sidenav"
      className={classNames(styles.sidenav, {
        [styles.sidenavExpanded]: isExpanded,
        [styles.sidenavAnimating]: isAnimating,
      })}
      style={
        {
          '--sidenav-width-collapsed': analyticsSidenavConfig.widthCollapsed,
          '--sidenav-width-expanded': analyticsSidenavConfig.widthExpanded,
          '--sidenav-transition-duration': `${analyticsSidenavConfig.transitionDuration}ms`,
        } as CSSProperties
      }
      {...(isExpandable
        ? {
            onMouseEnter: () => setIsExpanded(true),
            onMouseLeave: keepExpanded ? undefined : () => setIsExpanded(false),
          }
        : {})}
    >
      <Column className={styles.sections}>
        <Column.Row
          className={classNames(styles.expander, {
            [styles.expanderVisible]: !isExpanded,
          })}
          noHorizontalPadding
          noVerticalPadding
          noMinHeight
        >
          {isExpandable && (
            <Icon
              name="angle-double-right"
              size={24}
              aria-label="Expand"
              color="secondary"
            />
          )}
        </Column.Row>

        <Column.Row
          scrollable
          noHorizontalPadding
          noVerticalPadding
          noMinHeight
          className={styles.scrollable}
        >
          <AnalyticsSidenavSection
            section={favoritesSection}
            hidden={!isExpanded}
          >
            {favoritesSection.items.map((item) => (
              <AnalyticsSidenavItem key={item.title} item={item} />
            ))}
          </AnalyticsSidenavSection>

          {tiersSection.items.length > 1 && (
            <AnalyticsSidenavSection
              section={tiersSection}
              hidden={!isExpanded}
            >
              <AnalyticsSidenavTabs items={tiersSection.items} />
            </AnalyticsSidenavSection>
          )}

          {dashboardSections.map((section) => (
            <AnalyticsSidenavSection
              key={section.title}
              section={section}
              hidden={!isExpanded}
            >
              {section.items.map((item) => (
                <AnalyticsSidenavItem key={item.title} item={item} />
              ))}
            </AnalyticsSidenavSection>
          ))}
        </Column.Row>

        <Column.Row
          borderTop={isExpanded}
          noHorizontalPadding
          noVerticalPadding
          noMinHeight
        >
          <AnalyticsSidenavSection
            section={footerSection}
            hidden={isSmallScreen ?? false}
          >
            {footerSection.items.map((item) => (
              <AnalyticsSidenavItem key={item.title} item={item} />
            ))}
          </AnalyticsSidenavSection>
        </Column.Row>
      </Column>
    </nav>
  );
};

export const analyticsSidenavConfig = {
  widthCollapsed: '56px',
  widthExpanded: '258px',
  transitionDuration: 200,
};
